import { sortBy, round, sum } from 'lodash';
import { CardFacade, StoryFacade } from 'utils/facades';
import { CardData, StoryModel, StorySettingsOfCard } from 'types/story';
import { CARD_TYPE, COMPONENT_TYPE } from 'common/constants';
import { isFieldBlock } from 'common/utils/blocks/misc';
import { componentWalkFull } from 'common/utils/blocks/component-walk-full';
import { CountType, NavigationFacade } from 'common/utils/facades/navigation-card-facade';
import { CustomVariable, VariableType, VariableCategory, Variables, VariableDataType } from './types';

const genericVariablePattern = {
	[VariableType.fieldValue]: /^field\/(?<cardId>[^/]+)\/(?<componentId>[^/]+)$/, // match "field/:cardId/:componentId"
	[VariableType.characterScore]: /^character\/s\/(?<characterId>[^/]+)$/, // match "character/s/:characterId"
	[VariableType.characterScorePercent]: /^character\/sp\/(?<characterId>[^/]+)$/, // match "character/sp/:characterId"
};

const extractGenericVariableParts = {
	[VariableType.fieldValue]: (value: string) => {
		const match = value.match(genericVariablePattern[VariableType.fieldValue]);
		return match && match.groups ? { cardId: match.groups.cardId, componentId: match.groups?.componentId } : null;
	},
	[VariableType.characterScore]: (value: string) => {
		const match = value.match(genericVariablePattern[VariableType.characterScore]);
		return match && match.groups ? { characterId: match.groups.characterId } : null;
	},
	[VariableType.characterScorePercent]: (value: string) => {
		const match = value.match(genericVariablePattern[VariableType.characterScorePercent]);
		return match && match.groups ? { characterId: match.groups.characterId } : null;
	},
};

export const getGenericVariableName = {
	[VariableType.fieldValue]: (params: { cardId: string; componentId: string }) =>
		VariableType.fieldValue.replace(':cardId', params.cardId).replace(':componentId', params.componentId),
	[VariableType.characterScore]: (params: { characterId: string }) =>
		VariableType.characterScore.replace(':characterId', params.characterId),
	[VariableType.characterScorePercent]: (params: { characterId: string }) =>
		VariableType.characterScorePercent.replace(':characterId', params.characterId),
};

export const commonVariables: Record<
	| 'cardName'
	| 'date'
	| 'time'
	| 'datePublished'
	| 'score'
	| 'scorePercent'
	| 'cardsVisited'
	| 'cardsScored'
	| 'cardsAnswered'
	| 'totalAnswerCards'
	| 'totalScoreCards'
	| 'correct'
	| 'incorrect'
	| 'answersMax'
	| 'answersMin'
	| 'answersSelected'
	| 'storyAnswersSelected',
	VariableDataType
> = {
	cardName: {
		name: VariableType.cardName,
		title: 'Card name',
		placeholder: 'card name',
		description: 'Current card name',
		type: VariableType.cardName,
		category: VariableCategory.general,
	},
	date: {
		name: VariableType.date,
		title: 'Date',
		placeholder: 'date',
		description: 'Current date by user location',
		type: VariableType.date,
		category: VariableCategory.general,
	},
	time: {
		name: VariableType.time,
		title: 'Time',
		placeholder: 'time',
		description: 'Current time [hh]:[mm]:[ss] by user location',
		type: VariableType.time,
		category: VariableCategory.general,
	},
	datePublished: {
		name: VariableType.datePublished,
		title: 'Published date and time',
		placeholder: 'published time',
		description: 'The time and date your story published',
		type: VariableType.datePublished,
		category: VariableCategory.general,
	},
	score: {
		name: VariableType.score,
		title: 'Total score',
		placeholder: 'score',
		description: 'Total score received for the cards played',
		type: VariableType.score,
		category: VariableCategory.scoring,
	},
	scorePercent: {
		name: VariableType.scorePercent,
		title: 'Total score (%)',
		placeholder: 'score %',
		description: 'Total score percentage to the maximum (0-100)',
		type: VariableType.scorePercent,
		category: VariableCategory.scoring,
	},
	cardsVisited: {
		name: VariableType.cardsVisited,
		title: 'Total cards visited',
		placeholder: 'cards visited',
		description: 'Total number of cards that the user visited',
		type: VariableType.cardsVisited,
		category: VariableCategory.numbering,
	},
	cardsScored: {
		name: VariableType.cardsScored,
		title: 'Total cards scored',
		placeholder: 'cards scored',
		description: 'Total completed cards that can have a score (Trivia, True or False)',
		type: VariableType.cardsScored,
		category: VariableCategory.numbering,
	},
	cardsAnswered: {
		name: VariableType.cardsAnswered,
		title: 'Total cards answered',
		placeholder: 'cards answered',
		description: 'Total completed cards that can have an answer',
		type: VariableType.cardsAnswered,
		category: VariableCategory.numbering,
	},
	totalAnswerCards: {
		name: VariableType.totalAnswerCards,
		title: 'Total answer cards',
		placeholder: 'total answer cards',
		description: 'Total cards that can have an answer',
		type: VariableType.totalAnswerCards,
		category: VariableCategory.numbering,
	},
	totalScoreCards: {
		name: VariableType.totalScoreCards,
		title: 'Total score cards',
		placeholder: 'total score cards',
		description: 'Total cards that can have a score',
		type: VariableType.totalScoreCards,
		category: VariableCategory.numbering,
	},
	correct: {
		name: VariableType.correct,
		title: 'Total correct answers',
		placeholder: 'correct answers',
		description: 'Total correct answers sent',
		type: VariableType.correct,
		category: VariableCategory.scoring,
	},
	incorrect: {
		name: VariableType.incorrect,
		title: 'Total incorrect answers',
		placeholder: 'incorrect answers',
		description: 'Total incorrect answers sent',
		type: VariableType.incorrect,
		category: VariableCategory.scoring,
	},
	answersMax: {
		name: VariableType.answersMax,
		title: 'Max answers',
		placeholder: 'max answers',
		description: 'Maximum allowed answers to select in this card',
		type: VariableType.answersMax,
		category: VariableCategory.multipleAnswers,
	},
	answersMin: {
		name: VariableType.answersMin,
		title: 'Min answers',
		placeholder: 'min answers',
		description: 'Minimum required answers to select in this card',
		type: VariableType.answersMin,
		category: VariableCategory.multipleAnswers,
	},
	answersSelected: {
		name: VariableType.answersSelected,
		title: 'Total selected answers in the card',
		placeholder: 'selected answers',
		description: 'Total user answers selected in this card',
		type: VariableType.answersSelected,
		category: VariableCategory.multipleAnswers,
	},
	storyAnswersSelected: {
		name: VariableType.storyAnswersSelected,
		title: 'Total selected answers in the story',
		placeholder: 'selected story answers',
		description: 'Total user answers selected in the story',
		type: VariableType.storyAnswersSelected,
		category: VariableCategory.multipleAnswers,
	},
};

// ordered list of variables in order to render in <MentionSuggestions> component
const orderedVariables: VariableType[] = [
	VariableType.cardName,
	VariableType.date,
	VariableType.time,
	VariableType.datePublished,
	VariableType.cardsVisited,
	VariableType.cardsScored,
	VariableType.cardsAnswered,
	VariableType.totalAnswerCards,
	VariableType.totalScoreCards,
	VariableType.score,
	VariableType.scorePercent,
	VariableType.correct,
	VariableType.incorrect,
	VariableType.fieldValue,
	VariableType.characterScore,
	VariableType.characterScorePercent,
	VariableType.answersMax,
	VariableType.answersMin,
	VariableType.answersSelected,
	VariableType.storyAnswersSelected,
];

export const getAvailableVariables = ({ story }: { story: StoryModel }) => {
	const { settings } = new StoryFacade(story);
	const variables = new Set<VariableDataType>([
		commonVariables.cardName,
		commonVariables.date,
		commonVariables.time,
		commonVariables.datePublished,
		commonVariables.cardsVisited,
	]);

	// // NOTE: ---------- temporary add all
	// variables.add(commonVariables.cardsScored);
	// variables.add(commonVariables.cardsAnswered);
	// variables.add(commonVariables.score);
	// variables.add(commonVariables.scorePercent);
	// variables.add(commonVariables.correct);
	// variables.add(commonVariables.incorrect);
	// variables.add(commonVariables.answersMax);
	// variables.add(commonVariables.answersMin);
	// variables.add(commonVariables.answersSelected);
	// // ---------- temporary add all END

	const storyFacade = new StoryFacade(story as StoryModel);

	// Collect hardcoded variables based on cards
	storyFacade.cards.forEach(card => {
		const cFacade = new CardFacade(card);

		if (cFacade.hasFeature.answer) {
			variables.add(commonVariables.cardsAnswered);
			variables.add(commonVariables.storyAnswersSelected);
			variables.add(commonVariables.totalAnswerCards);
		}

		if (cFacade.hasFeature.score) {
			variables.add(commonVariables.cardsScored);
			variables.add(commonVariables.totalScoreCards);
		}

		if (cFacade.hasFeature.score || cFacade.hasFeature.answerScore) {
			variables.add(commonVariables.score);
			variables.add(commonVariables.scorePercent);
		}

		if (cFacade.hasFeature.correct) {
			variables.add(commonVariables.correct);
			variables.add(commonVariables.incorrect);
		}

		if (cFacade.hasFeature.multipleAnswer && Array.isArray(settings?.cards?.[cFacade.cardId]?.answersMinMax)) {
			variables.add(commonVariables.answersMax);
			variables.add(commonVariables.answersMin);
			variables.add(commonVariables.answersSelected);
		}

		switch (card.type) {
			case CARD_TYPE.FORM:
				// collect field variables
				componentWalkFull({
					elements: cFacade.elements,
					symbols: storyFacade.symbols,
					callback: function walk({ component: c }) {
						if (isFieldBlock(c) && c.type !== COMPONENT_TYPE.CHECKBOX) {
							const fieldName = c.uiConfig.editorProps.name;
							variables.add({
								name: getGenericVariableName[VariableType.fieldValue]({
									cardId: card._id,
									componentId: c._id,
								}),
								title: `${card.name}/field/${fieldName}`,
								description: 'The text the user filled in this input',
								placeholder: fieldName,
								type: VariableType.fieldValue,
								category: VariableCategory.leadsForm,
							});
						}
					},
				});
				break;
			case CARD_TYPE.NAVIGATION: {
				// collect character points vars
				const navFacade = new NavigationFacade(card);
				if (settings && navFacade.getCountType(settings) === CountType.character) {
					Object.entries(navFacade.characters).forEach(([characterId, value]) => {
						const characterName = value.name;

						variables.add({
							name: getGenericVariableName[VariableType.characterScore]({ characterId }),
							title: `${card.name}/character-points/${characterName} (num)`,
							description: 'The total user points for a particular character',
							placeholder: `${characterName} (num)`,
							type: VariableType.characterScore,
							category: VariableCategory.characters,
						});

						variables.add({
							name: getGenericVariableName[VariableType.characterScorePercent]({ characterId }),
							title: `${card.name}/character-points/${value.name} (%)`,
							description: 'The total user percentage for a particular character',
							placeholder: `${characterName} (%)`,
							type: VariableType.characterScorePercent,
							category: VariableCategory.characters,
						});
					});
				}
				break;
			}
			default:
				break;
		}
	});

	// Collect custom variables
	const settingsVars: Variables = [
		// NOTE: temporary till there is no UI in admin to add custom variable
		// { name: 'c/custom1', title: 'Custom variable 1', value: 'Static custom value' },
		// { name: 's/source1', title: 'Custom source variable 2', value: 'Initial source value', source: 'https://' },
	];

	// add custom source story vars
	settings?.variables?.source?.forEach(source => {
		source.variables.forEach(v => {
			settingsVars.push({ ...v, source: source.url });
		});
	});

	// add custom story vars
	settings.variables?.custom?.forEach(v => {
		settingsVars.push(v);
	});

	// add story settings vars
	settingsVars.forEach(v => {
		variables.add(v);
	});

	return sortBy([...variables.values()], ({ type }) => (type === null ? -1 : orderedVariables.indexOf(type)));
};

// TODO: optimization (do less re-computes as possible. now it gets all values for every inject fn call)
export function injectVariablesValue({
	html,
	story,
	card,
	storyCardSettings,
	totalSelectedAnswers,
}: {
	html: string;
	story: StoryModel;
	card: CardData;
	storyCardSettings: StorySettingsOfCard;
	totalSelectedAnswers: number;
}) {
	const div = document.createElement('div');
	div.innerHTML = html;
	const nodes = div.querySelectorAll<HTMLElement>('[data-mention]');
	const storyFacade = new StoryFacade(story);
	const cardFacade = new CardFacade(card);
	const totalScore = storyFacade.storyScore[CountType.score]();
	const totalCharacterPoints = storyFacade.storyScore[CountType.character]();
	const variables: VariableType[] = [];

	[...nodes].forEach(draftNode => {
		const variableName = draftNode.dataset.mention as VariableType;
		variables.push(variableName);

		// Variable value
		let value: string | number | null = null;

		switch (variableName) {
			case VariableType.cardName: {
				const cardName = storyFacade.getDataByCardId(card._id).card?.name;
				if (cardName !== undefined) value = cardName;
				break;
			}
			case VariableType.date: {
				value = new Date().toLocaleDateString().replaceAll('/', '.');
				break;
			}
			case VariableType.time: {
				value = new Date().toLocaleTimeString();
				break;
			}
			case VariableType.datePublished: {
				const d = new Date(storyFacade.story.updatedAt);
				const format = '2-digit';
				// use template string instead of locale options to support old browsers
				value = `${d
					.toLocaleDateString(undefined, {
						day: format,
						month: format,
						year: 'numeric',
						hour: format,
						minute: format,
						second: format,
					})
					.replaceAll('/', '.')
					.replace(',', '')}`;
				break;
			}
			case VariableType.cardsScored:
				value = totalScore.cardsScored;
				break;
			case VariableType.cardsAnswered:
				value = totalScore.cardsAnswered;
				break;
			case VariableType.cardsVisited:
				value = storyFacade.totalVisitedCards;
				break;
			case VariableType.totalAnswerCards:
				value = storyFacade.cards.filter(c => new CardFacade(c).hasFeature.answer).length;
				break;
			case VariableType.totalScoreCards:
				value = storyFacade.cards.filter(c => new CardFacade(c).hasFeature.score).length;
				break;
			case VariableType.correct:
				value = totalScore.answersCorrect;
				break;
			case VariableType.incorrect:
				value = totalScore.cardsScored - totalScore.answersCorrect;
				break;
			case VariableType.answersMin:
				value = CardFacade.getAnswersMin(cardFacade.type, storyCardSettings);
				break;
			case VariableType.answersMax:
				value = CardFacade.getAnswersMax(cardFacade.type, storyCardSettings);
				break;
			case VariableType.answersSelected:
				value = totalSelectedAnswers;
				break;
			case VariableType.storyAnswersSelected:
				value = totalScore.totalAnswersSelected;
				break;
			case VariableType.score:
				value = totalScore.scoreTotal;
				break;
			case VariableType.scorePercent:
				value = round(totalScore.scoreRelative, 2);
				break;
			default: {
				if (!variableName) {
					break;
				}

				// Generic variables
				if (variableName.match(genericVariablePattern[VariableType.fieldValue])) {
					const fieldValParts = extractGenericVariableParts[VariableType.fieldValue](variableName)!;
					const { cardId, componentId } = fieldValParts;
					const fieldVal = componentId ? storyFacade.submittedFormData[cardId]?.[componentId] : null;
					if (fieldVal !== undefined) value = fieldVal;
					break;
				}
				if (variableName.match(genericVariablePattern[VariableType.characterScore])) {
					/*
					 todo: Think what I can do with a character results.

					 const characterResults = this.storyScore[CountType.character]();
					 1. When NavigationCard last time ran I can store how much points and which character won
					 and display there points as $score or $points, or I can just remember eventId and cardId which led
					 user to current page
					 2. + It is possible by demand somehow show how much points each character got.
					 */
					const characterParts = extractGenericVariableParts[VariableType.characterScore](variableName)!;
					value = totalCharacterPoints[characterParts.characterId] ?? 0;
					break;
				}
				if (variableName.match(genericVariablePattern[VariableType.characterScorePercent])) {
					const characterParts =
						extractGenericVariableParts[VariableType.characterScorePercent](variableName)!;
					const charPoints = totalCharacterPoints[characterParts.characterId] ?? 0;
					const totalPoints = sum(Object.values(totalCharacterPoints));
					value = `${charPoints > 0 ? round((charPoints / totalPoints) * 100, 2) : 0}%`;
					break;
				}

				// Custom variables
				const customVariables = storyFacade.settings.variables?.custom;
				const customVariable = customVariables?.find(v => v.name === variableName);
				if (customVariable) {
					value = customVariable.value;
					break;
				}
				const sourceVariables = storyFacade.settings.variables?.source;
				const sourceVariable = sourceVariables?.reduce<null | { url: string; variable: CustomVariable }>(
					(acc, source) => {
						if (!acc) {
							const variable = source.variables.find(v => v.name === variableName);
							if (variable) return { url: source.url, variable };
						}
						return acc;
					},
					null
				);
				if (sourceVariable) {
					/*
					  TODO
					  Source variables. Not implement yet, but prepared for a future implementation.
					  By idea, all values for a source variables should be fetched once, before story mound and then
					  retrieved from story settings at redux
					 */
					value = sourceVariable.variable.value;
					break;
				}
			}
		}

		draftNode.textContent = value !== null && value !== undefined ? `${value}` : '';
	});

	return {
		variables,
		html: div.innerHTML,
	};
}
